/**
 * 화면 아래에 찍는 버전. (2026-08-17)
 *
 * 값은 빌드 시점에 박힌다. CI 가 Docker build-arg 로 넘기고,
 * vite 가 VITE_ 로 시작하는 환경변수를 번들에 넣는다.
 * (deploy.yml 버전 결정 스텝 · frontend/Dockerfile)
 *
 * 🔴 런타임에 API 로 받아오지 않는다
 *   프론트는 nginx 가 정적 파일로 주는 것이라 스스로의 버전을 안다.
 *   백엔드에 물어보면 둘이 어긋났을 때(프론트만 배포된 상태 등) 알 수 없다.
 *
 * 형식
 *   2026.08.17.3   그날 세 번째 배포
 *   a9b292c        커밋 앞 7자
 *
 * 왜 시맨틱 버전이 아닌가
 *   배포할 때마다 minor 인지 patch 인지 사람이 판단해야 한다.
 *   혼자 쓰는 대시보드에서 그 판단은 대체로 미뤄지고 결국 버전이 안 올라간다.
 *   날짜는 자동으로 정해지고, 문제가 생겼을 때 가장 먼저 궁금한
 *   "언제 배포된 화면을 보고 있나" 에 바로 답한다.
 */

export interface AppVersion {
  /** 예: 2026.08.17.3 */
  version: string;
  /** 커밋 앞 7자. 예: a9b292c */
  commit: string;
}

/**
 * 빌드에 박힌 버전을 읽는다. 값이 없으면 null.
 *
 * 로컬 개발에서는 언제나 null 이다 — `npm run dev` 는 CI 를 거치지 않는다.
 * 그때 "개발" 같은 가짜 값을 넣지 않는다. 가짜 버전이 화면에 뜨면
 * 그것이 진짜인지 아닌지를 매번 확인해야 한다. **없으면 안 보이는 편이 낫다.**
 *
 * @param env import.meta.env. 테스트에서 값을 넣어보려고 인자로 받는다
 */
export function readAppVersion(env: Record<string, unknown>): AppVersion | null {
  const version = asText(env.VITE_APP_VERSION);
  if (version === null) {
    return null;
  }

  return {
    version,
    // 커밋은 없어도 버전만 보여준다. 전체 SHA 가 오면 앞 7자만 쓴다.
    commit: asText(env.VITE_APP_COMMIT)?.slice(0, 7) ?? '',
  };
}

/** 문자열이고 비어 있지 않은 값만 통과시킨다. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * 화면에 찍을 한 줄.
 *
 *   formatAppVersion({version: '2026.08.17.3', commit: 'a9b292c'})  → '2026.08.17.3 · a9b292c'
 *   formatAppVersion({version: '2026.08.17.3', commit: ''})         → '2026.08.17.3'
 */
export function formatAppVersion(value: AppVersion): string {
  return value.commit === '' ? value.version : `${value.version} · ${value.commit}`;
}
